import { useEffect, useRef, type RefObject } from 'react';

export const PDF_MIN_ZOOM = 1;

export const PDF_MAX_ZOOM = 3;

type Pinch = { distance: number; zoom: number; x: number; y: number; scrollLeft: number; scrollTop: number };

const distanceOf = (touches: TouchList) => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

const clampZoom = (zoom: number) => Math.min(PDF_MAX_ZOOM, Math.max(PDF_MIN_ZOOM, zoom));

export const usePinchZoom = (
	scroller: RefObject<HTMLElement | null>,
	content: RefObject<HTMLElement | null>,
	zoom: number,
	onZoom: (zoom: number) => void,
	active: boolean,
) => {
	const latest = useRef({ zoom, onZoom });
	useEffect(() => {
		latest.current = { zoom, onZoom };
	}, [zoom, onZoom]);

	useEffect(() => {
		const area = scroller.current;
		if (!active || !area) return;
		let pinch: Pinch | null = null;
		let live = 1;

		const start = (event: TouchEvent) => {
			if (event.touches.length !== 2) return;
			const box = area.getBoundingClientRect();
			pinch = {
				distance: distanceOf(event.touches),
				zoom: latest.current.zoom,
				x: (event.touches[0].clientX + event.touches[1].clientX) / 2 - box.left,
				y: (event.touches[0].clientY + event.touches[1].clientY) / 2 - box.top,
				scrollLeft: area.scrollLeft,
				scrollTop: area.scrollTop,
			};
			live = pinch.zoom;
		};

		const move = (event: TouchEvent) => {
			if (!pinch || event.touches.length !== 2) return;
			event.preventDefault();
			live = clampZoom((pinch.zoom * distanceOf(event.touches)) / pinch.distance);
			const node = content.current;
			if (!node) return;
			node.style.transformOrigin = `${pinch.scrollLeft + pinch.x}px ${pinch.scrollTop + pinch.y}px`;
			node.style.transform = `scale(${live / pinch.zoom})`;
		};

		const end = (event: TouchEvent) => {
			if (!pinch || event.touches.length >= 2) return;
			const { zoom: from, x, y, scrollLeft, scrollTop } = pinch;
			pinch = null;
			const node = content.current;
			if (node) {
				node.style.transform = '';
				node.style.transformOrigin = '';
			}
			const ratio = live / from;
			if (Math.abs(ratio - 1) < 0.02) return;
			latest.current.onZoom(live);
			requestAnimationFrame(() =>
				requestAnimationFrame(() => {
					area.scrollLeft = (scrollLeft + x) * ratio - x;
					area.scrollTop = (scrollTop + y) * ratio - y;
				}),
			);
		};

		const blockNativeZoom = (event: Event) => event.preventDefault();

		area.addEventListener('touchstart', start, { passive: true });
		area.addEventListener('touchmove', move, { passive: false });
		area.addEventListener('touchend', end);
		area.addEventListener('touchcancel', end);
		area.addEventListener('gesturestart', blockNativeZoom);
		return () => {
			area.removeEventListener('touchstart', start);
			area.removeEventListener('touchmove', move);
			area.removeEventListener('touchend', end);
			area.removeEventListener('touchcancel', end);
			area.removeEventListener('gesturestart', blockNativeZoom);
		};
	}, [scroller, content, active]);
};
